import React from 'react';
import { motion } from 'framer-motion';
import { CirclePlus } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  formatSheetRowPriceInputValue,
  formatSheetRowPromotionDiscountInputValue,
  parseSheetPriceInputToNumber,
  parseSheetPromotionDiscountInputToNumber,
} from '../../../services/materialCommerceSheetService.ts';
import type { CatalogPickOption } from '../../Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsCatalogPick.ts';
import addMaterialPopupStyles from '../../MarketPlace/CalculadoraDeMateriales/AddCustomMaterialPopup.module.scss';
import {
  coerceRawUnitToMaterialSelectValue,
  MATERIAL_UNIT_SELECT_OPTIONS,
} from '../../../utils/marketplace/materialUnitSelectOptions.ts';
import CustomSelect from '../../../pages/ProjectDocumentation/components/CustomSelect.tsx';
import ModalOverlay from '../../UI/ModalOverlay/ModalOverlay.tsx';
import styles from '../CommerceSheetReviewStep.module.scss';
import { addMaterialPopupVariants } from './commerceSheetReviewConstants.ts';
import type { ManualAddMaterialFieldErrors } from './commerceManualAddMaterialValidation.ts';

export interface CommerceSheetManualAddMaterialModalProps {
  addModalOpen: boolean;
  closeAddMaterialModal: () => void;
  addModalSubmitting: boolean;
  submitAddMaterialModal: () => void;
  addModalError: string;
  globalCatalogFetchError: string;
  categorySelectOptions: { value: string; label: string }[];
  modCategoryId: string;
  onModCategoryChange: (v: string) => void;
  modRawName: string;
  setModRawName: (v: string) => void;
  catalogMaterialPick: CatalogPickOption | null;
  setCatalogMaterialPick: React.Dispatch<React.SetStateAction<CatalogPickOption | null>>;
  setAddModalError: (v: string) => void;
  globalCatalogStatus: 'idle' | 'loading' | 'ready' | 'error';
  globalCatalogMatches: CatalogPickOption[];
  modDesc: string;
  setModDesc: (v: string) => void;
  modPromo: string;
  setModPromo: (v: string) => void;
  modDisc: string;
  setModDisc: (v: string) => void;
  modPrice: string;
  setModPrice: (v: string) => void;
  modUnit: string;
  setModUnit: (v: string) => void;
  modUnquoted: boolean;
  setModUnquoted: (v: boolean) => void;
  modVat: boolean;
  setModVat: (v: boolean) => void;
  setModCategoryId: (v: string) => void;
  overlayZIndex?: number;
  fieldErrors: ManualAddMaterialFieldErrors;
  saveButtonDisabled: boolean;
}

export const CommerceSheetManualAddMaterialModal: React.FC<CommerceSheetManualAddMaterialModalProps> = ({
  addModalOpen,
  closeAddMaterialModal,
  addModalSubmitting,
  submitAddMaterialModal,
  addModalError,
  globalCatalogFetchError,
  categorySelectOptions,
  modCategoryId,
  onModCategoryChange,
  modRawName,
  setModRawName,
  catalogMaterialPick,
  setCatalogMaterialPick,
  setAddModalError,
  globalCatalogStatus,
  globalCatalogMatches,
  modDesc,
  setModDesc,
  modPromo,
  setModPromo,
  modDisc,
  setModDisc,
  modPrice,
  setModPrice,
  modUnit,
  setModUnit,
  modUnquoted,
  setModUnquoted,
  modVat,
  setModVat,
  setModCategoryId,
  overlayZIndex = 32,
  fieldErrors,
  saveButtonDisabled,
}) => {
  const req = (
    <span className={addMaterialPopupStyles.requiredStar} aria-hidden="true">
      *
    </span>
  );
  const showCategoryStar = !catalogMaterialPick && !String(modCategoryId).trim();
  const showNameStar = !modRawName.trim();
  const showUnitStar =
    !catalogMaterialPick && !coerceRawUnitToMaterialSelectValue(modUnit);
  const showPriceStar =
    !modUnquoted &&
    (!modPrice.trim() || parseSheetPriceInputToNumber(modPrice.trim()) == null);

  return (
    <ModalOverlay
      isOpen={addModalOpen}
      onClose={closeAddMaterialModal}
      preventClose={addModalSubmitting}
      zIndex={overlayZIndex}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={addMaterialPopupVariants}
        className={addMaterialPopupStyles.popup}
      >
        <div className={addMaterialPopupStyles.popup_header}>
          <div>
            <h2 className={addMaterialPopupStyles.popup_header_title}>Agregar material al catálogo</h2>
          </div>
          <button
            type="button"
            className={addMaterialPopupStyles.popup_header_cancelBtn}
            onClick={closeAddMaterialModal}
            disabled={addModalSubmitting}
          >
            Cancelar
          </button>
        </div>
        <form
          className={addMaterialPopupStyles.popup_form}
          onSubmit={(e) => {
            e.preventDefault();
            submitAddMaterialModal();
          }}
        >
          <div className={addMaterialPopupStyles.popup_formBody}>
            {addModalError ? (
              <p className={styles.error} role="alert">
                {addModalError}
              </p>
            ) : null}
            <div className={addMaterialPopupStyles.formSection}>
              <h3 className={addMaterialPopupStyles.formSectionTitle}>Datos del material</h3>
              {globalCatalogFetchError ? <p className={styles.error}>{globalCatalogFetchError}</p> : null}
              <div className={addMaterialPopupStyles.formGroup}>
                <label htmlFor="sheet-add-material-category">
                  Categoría
                  {showCategoryStar ? req : null}
                </label>
                <CustomSelect
                  value={modCategoryId}
                  onChange={onModCategoryChange}
                  options={categorySelectOptions}
                  placeholder="Seleccioná una categoría"
                  searchable
                  searchPlaceholder="Buscar categoría…"
                  error={fieldErrors.category}
                  outlineInvalid={Boolean(fieldErrors.category)}
                />
              </div>
              <div className={addMaterialPopupStyles.formGroup}>
                <label htmlFor="sheet-add-material-name">
                  Nombre
                  {showNameStar ? req : null}
                </label>
                <input
                  id="sheet-add-material-name"
                  type="text"
                  autoComplete="off"
                  aria-required={showNameStar}
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={
                    fieldErrors.name ? 'sheet-add-material-name-error' : undefined
                  }
                  className={fieldErrors.name ? addMaterialPopupStyles.inputOutlineInvalid : undefined}
                  value={modRawName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setModRawName(v);
                    if (catalogMaterialPick && v.trim() !== catalogMaterialPick.materialName.trim()) {
                      setCatalogMaterialPick(null);
                    }
                  }}
                  placeholder="Como en tu listado o escribí para buscar sugerencias (2 letras o más)"
                />
                {fieldErrors.name ? (
                  <p
                    id="sheet-add-material-name-error"
                    className={addMaterialPopupStyles.fieldErrorMessage}
                    role="alert"
                  >
                    {fieldErrors.name}
                  </p>
                ) : null}
                <p className={styles.catalogGlobalHint}>
                  {globalCatalogStatus === 'loading'
                    ? 'Cargando sugerencias…'
                    : globalCatalogStatus === 'error'
                      ? 'No se pudieron cargar sugerencias; igual podés cargar el material a mano.'
                      : catalogMaterialPick
                        ? 'Elegiste un producto de la lista. Para cargar otro distinto, cambiá el nombre o tocá «Cambiar».'
                        : modRawName.trim().length < 2
                          ? 'Escribí al menos 2 letras para ver sugerencias.'
                          : globalCatalogMatches.length === 0
                            ? 'No hay sugerencias con ese texto y categoría.'
                            : `${globalCatalogMatches.length} coincidencia${globalCatalogMatches.length === 1 ? '' : 's'}`}
                </p>
                {catalogMaterialPick ? (
                  <div className={styles.catalogPickBanner}>
                    <span className={styles.catalogPickBannerText}>
                      <strong>{catalogMaterialPick.materialName}</strong>
                      <span className={styles.catalogPickBannerCat}> · {catalogMaterialPick.categoryName}</span>
                    </span>
                    <button
                      type="button"
                      className={styles.catalogPickBannerClear}
                      onClick={() => {
                        setCatalogMaterialPick(null);
                      }}
                    >
                      Cambiar
                    </button>
                  </div>
                ) : null}
                {!catalogMaterialPick &&
                globalCatalogStatus === 'ready' &&
                modRawName.trim().length >= 2 &&
                globalCatalogMatches.length > 0 ? (
                  <div className={styles.catalogGlobalMatchList} role="listbox" aria-label="Materiales sugeridos">
                    {globalCatalogMatches.map((o) => {
                      const key = `${o.categoryId}\t${o.materialId}`;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="option"
                          aria-selected={false}
                          className={styles.catalogGlobalMatchRow}
                          onClick={() => {
                            setCatalogMaterialPick(o);
                            setModCategoryId(String(o.categoryId));
                            setModRawName(o.materialName);
                            setAddModalError('');
                          }}
                        >
                          <span className={styles.catalogGlobalMatchName}>{o.materialName}</span>
                          <span className={styles.catalogGlobalMatchCat}>{o.categoryName}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              {!catalogMaterialPick ? (
                <div className={addMaterialPopupStyles.formGroup}>
                  <label htmlFor="sheet-add-material-desc">Descripción</label>
                  <TextareaAutosize
                    id="sheet-add-material-desc"
                    value={modDesc}
                    onChange={(e) => setModDesc(e.target.value)}
                    minRows={2}
                  />
                </div>
              ) : null}
              <div className={addMaterialPopupStyles.formGroup}>
                <label htmlFor="sheet-add-material-promo">Bonificación</label>
                <input
                  id="sheet-add-material-promo"
                  type="text"
                  value={modPromo}
                  onChange={(e) => setModPromo(e.target.value)}
                />
              </div>
              <div className={addMaterialPopupStyles.formGroup}>
                <label htmlFor="sheet-add-material-disc">Descuento de promoción (%)</label>
                <input
                  id="sheet-add-material-disc"
                  type="text"
                  inputMode="decimal"
                  value={modDisc}
                  onChange={(e) => setModDisc(e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) {
                      setModDisc('');
                      return;
                    }
                    const n = parseSheetPromotionDiscountInputToNumber(raw);
                    if (n != null) setModDisc(formatSheetRowPromotionDiscountInputValue(n));
                  }}
                  aria-invalid={Boolean(fieldErrors.discount)}
                  className={fieldErrors.discount ? addMaterialPopupStyles.inputOutlineInvalid : undefined}
                />
                {fieldErrors.discount ? (
                  <p className={addMaterialPopupStyles.fieldErrorMessage} role="alert">
                    {fieldErrors.discount}
                  </p>
                ) : null}
              </div>
              <div className={addMaterialPopupStyles.formGroup}>
                <label htmlFor="sheet-add-material-price">
                  Precio de lista
                  {showPriceStar ? req : null}
                </label>
                <input
                  id="sheet-add-material-price"
                  type="text"
                  inputMode="decimal"
                  value={modPrice}
                  onChange={(e) => setModPrice(e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    if (!raw) {
                      setModPrice('');
                      return;
                    }
                    const n = parseSheetPriceInputToNumber(raw);
                    if (n != null) setModPrice(formatSheetRowPriceInputValue(n));
                  }}
                  disabled={modUnquoted}
                  aria-required={showPriceStar}
                  aria-invalid={Boolean(fieldErrors.price)}
                  className={fieldErrors.price ? addMaterialPopupStyles.inputOutlineInvalid : undefined}
                />
                {fieldErrors.price ? (
                  <p className={addMaterialPopupStyles.fieldErrorMessage} role="alert">
                    {fieldErrors.price}
                  </p>
                ) : null}
              </div>
              {!catalogMaterialPick ? (
                <div className={addMaterialPopupStyles.formGroup}>
                  <label>
                    Unidad
                    {showUnitStar ? req : null}
                  </label>
                  <CustomSelect
                    value={modUnit}
                    onChange={setModUnit}
                    options={MATERIAL_UNIT_SELECT_OPTIONS}
                    placeholder="Seleccioná una unidad"
                    searchable
                    searchPlaceholder="Buscar unidad…"
                    error={fieldErrors.unit}
                    outlineInvalid={Boolean(fieldErrors.unit)}
                  />
                </div>
              ) : (
                <p className={styles.catalogLinkedUnitNote}>La unidad corresponde al producto que elegiste.</p>
              )}
              <div className={addMaterialPopupStyles.formCheckboxList}>
                <label htmlFor="sheet-add-material-unquoted">
                  <input
                    id="sheet-add-material-unquoted"
                    type="checkbox"
                    checked={modUnquoted}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setModUnquoted(on);
                      if (on) {
                        setModPrice('');
                        setModVat(false);
                      } else {
                        setModVat(false);
                      }
                    }}
                  />
                  No informar precio
                </label>
                {!modUnquoted ? (
                  <label htmlFor="sheet-add-material-vat">
                    <input
                      id="sheet-add-material-vat"
                      type="checkbox"
                      checked={modVat}
                      onChange={(e) => setModVat(e.target.checked)}
                    />
                    IVA incluido en el precio
                  </label>
                ) : null}
              </div>
            </div>
          </div>
          <div className={addMaterialPopupStyles.buttonGroup}>
            <button
              type="submit"
              disabled={saveButtonDisabled}
              className={addMaterialPopupStyles.buttonGroup_acceptBtn}
            >
              <CirclePlus size={18} />
              {addModalSubmitting ? 'Guardando…' : 'Guardar en mi catálogo'}
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  );
};
